/**
 * Talks to the browser extension's bridge content script on this page, for
 * pairing. The page asks whether the extension is here, and hands it a fresh
 * device token together with this cell's API base; the token goes nowhere
 * else. Messages stay on this window and this origin.
 */
import { API_BASE } from "./session.ts";

type BridgeReply = { type: "sable-ext-present"; paired: boolean } | { type: "sable-ext-paired"; ok: boolean; error?: string };

function ask<T extends BridgeReply["type"]>(message: Record<string, unknown>, expect: T, timeoutMs: number): Promise<Extract<BridgeReply, { type: T }> | null> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => { window.removeEventListener("message", onMessage); resolve(null); }, timeoutMs);
    function onMessage(e: MessageEvent) {
      if (e.source !== window || e.origin !== location.origin) return;
      const data = e.data as BridgeReply | null;
      if (data?.type !== expect) return;
      clearTimeout(timer);
      window.removeEventListener("message", onMessage);
      resolve(data as Extract<BridgeReply, { type: T }>);
    }
    window.addEventListener("message", onMessage);
    window.postMessage(message, location.origin);
  });
}

/** Whether the extension is installed in this browser (its bridge answers within a moment). */
export async function extensionPresent(): Promise<boolean> {
  return (await ask({ type: "sable-ext-ping" }, "sable-ext-present", 600)) !== null;
}

/**
 * What to paste into the extension's popup when it can't be reached from the
 * page: the cell's API base with the token as its fragment.
 */
export function pairingCode(token: string): string {
  return `${location.origin}${API_BASE}#${token}`;
}

/** Hands a device token to the extension; resolves once it has checked in with it. */
export async function pairExtension(token: string, name: string): Promise<{ ok: boolean; error?: string }> {
  const reply = await ask(
    { type: "sable-ext-pair", token, name, apiBase: `${location.origin}${API_BASE}` },
    "sable-ext-paired",
    15_000,
  );
  return reply ?? { ok: false, error: "The extension didn't answer" };
}
